import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useNavigation, useRouter } from 'expo-router';

import { HapticNotificationFeedbackStyle, triggerHapticNotificationFeedback } from '@sg/garnish';
import { IngredientAssetVersion } from '@sg/graphql-schema';

import {
  Bundle,
  type BundleContentHandle,
  type BundleMachineTypes,
  type BundleSelectProduct,
  useBundleMachine,
  useInitBundleMachine,
} from '@order/components-menu';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { NotFoundView } from '../../components';
import { useDeliveryOrderInFlight } from '../../hooks';
import { handleHardwareBackButtonPress, useNavigateBack } from '../../navigation';
import {
  useBundleAddedToBagNotice,
  useBundleAddToBagActors,
  useBundleFailedToAddToBagNotice,
  useBundleRemoteData,
} from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const BundleScreen = () => {
  const isBundleEnabled = useFeatureFlag('CELS-5880-order-product-bundles-enabled');
  const navigateBack = useNavigateBack();

  if (!isBundleEnabled) {
    return (
      <Bundle.Container onDismiss={navigateBack}>
        <NotFoundView withoutFooter />

        <Bundle.CloseButton onPress={navigateBack} />
      </Bundle.Container>
    );
  }

  return <BundleScreenLoader />;
};

// ─────────────────────────────────────────────────────────────────────────────

const BundleScreenLoader = () => {
  const router = useRouter();
  const navigateBack = useNavigateBack();

  const { content, products, productsBySlug, restaurantSlug, isDelivery, hasLocation, isLoading } =
    useBundleRemoteData();

  // ─── Handlers ────────────────────────────────────────────────────────

  // NOTE: Pushed rather than redirected, so this screen stays underneath and
  // the location search can present as a sheet over it.
  const navigateToLocationSearch = useCallback(() => {
    router.push('/bundle/search-location');
  }, [router]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    if (hasLocation) return;

    navigateToLocationSearch();
  }, [hasLocation, navigateToLocationSearch]);

  // ─────────────────────────────────────────────────────────────────────

  if (!hasLocation) {
    return (
      <Bundle.Container onDismiss={navigateBack}>
        <Bundle.NoLocation onSelectLocation={navigateToLocationSearch} />

        <Bundle.CloseButton onPress={navigateBack} />
      </Bundle.Container>
    );
  }

  if (isLoading) {
    return (
      <Bundle.Container onDismiss={navigateBack}>
        <Bundle.Loading />

        <Bundle.CloseButton onPress={navigateBack} />
      </Bundle.Container>
    );
  }

  if (!restaurantSlug) {
    return (
      <Bundle.Container onDismiss={navigateBack}>
        <NotFoundView withoutFooter />

        <Bundle.CloseButton onPress={navigateBack} />
      </Bundle.Container>
    );
  }

  return (
    <BundleScreenReady
      // NOTE: Keyed by location, so a location change starts a fresh bundle machine.
      key={`${restaurantSlug}:${isDelivery}`}
      content={content}
      products={products}
      productsBySlug={productsBySlug}
      restaurantSlug={restaurantSlug}
      isDelivery={isDelivery}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const BundleScreenReady = (props: Props) => {
  const { content, products, productsBySlug, restaurantSlug, isDelivery } = props;

  const router = useRouter();
  const navigation = useNavigation();
  const navigateBack = useNavigateBack();
  const isUpsellPricingEnabled = useFeatureFlag('CELS-5026-customization-upsell-pricing-enabled');
  const showAddedToBagNotice = useBundleAddedToBagNotice({ productsBySlug });
  const showFailedToAddToBagNotice = useBundleFailedToAddToBagNotice();
  const { customization, refreshBag, captureDeliveryDetails } = useBundleAddToBagActors();
  const { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen } =
    useDeliveryOrderInFlight({ requestPolicy: 'cache-only' });

  // ─── Refs ────────────────────────────────────────────────────────────

  const contentRef = useRef<BundleContentHandle>(null);

  // ─── Machine ─────────────────────────────────────────────────────────

  const handleProductConfirmed = useCallback(() => {
    contentRef.current?.scrollToEnd();
  }, []);

  const handleAddedToBag = useCallback(
    (_actionArgs: unknown, summary: AddToBagSummary) => {
      void triggerHapticNotificationFeedback(HapticNotificationFeedbackStyle.Success);
      showAddedToBagNotice(summary);
      router.dismissTo('/bag');
    },
    [router, showAddedToBagNotice],
  );

  const bundleActorRef = useInitBundleMachine(
    {
      restaurantSlug,
      isDelivery,
      ingredientAssetVersion: IngredientAssetVersion.V2,
      isUpsellPricingEnabled,
    },
    {
      actors: { customization, refreshBag },
      actions: {
        onProductConfirmed: handleProductConfirmed,
        onAddedToBag: handleAddedToBag,
        onFailedToAddToBag: showFailedToAddToBagNotice,
      },
    },
  );

  const {
    selectedProducts,
    selectedCount,
    canSelectMore,
    pendingProduct,
    isSelecting,
    isLocked,
    addToBagStatus,
    settledProductCount,
    openSelect,
    selectProduct,
    confirmSelect,
    closeSelect,
    removeSelected,
    addToBag,
  } = useBundleMachine(bundleActorRef);

  // ─── Derived ─────────────────────────────────────────────────────────

  const selectedItems = useMemo(() => {
    return selectedProducts
      .map(({ productSlug, selectionId, outOfStockIngredientNames }) => {
        const product = productsBySlug.get(productSlug);

        if (!product) return;

        return { ...product, selectionId, outOfStockIngredientNames };
      })
      .filter((product) => product !== undefined);
  }, [productsBySlug, selectedProducts]);

  const hasDeliveryOrderInFlight = isDelivery && Boolean(inFlightDeliveryOrderId);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleDismiss = useCallback(() => {
    if (isLocked) return;

    navigateBack();
  }, [isLocked, navigateBack]);

  const handleAddToBag = useCallback(() => {
    if (hasDeliveryOrderInFlight) {
      navigateToDeliveryOrderInFlightScreen();

      return;
    }

    captureDeliveryDetails();
    addToBag();
  }, [
    addToBag,
    captureDeliveryDetails,
    hasDeliveryOrderInFlight,
    navigateToDeliveryOrderInFlightScreen,
  ]);

  const handleSelect = useCallback(
    (product: BundleSelectProduct) => {
      selectProduct({ productSlug: product.slug });
    },
    [selectProduct],
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    navigation.setOptions({ gestureEnabled: !isLocked });
  }, [isLocked, navigation]);

  useEffect(() => {
    return handleHardwareBackButtonPress(() => isLocked);
  }, [isLocked]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <Bundle.Container onDismiss={handleDismiss}>
        <Bundle.Content ref={contentRef}>
          <Bundle.Hero imageUrl={content.imageUrl} imageA11y={content.imageA11y} />

          <Bundle.Copy name={content.name} description={content.description} />

          <Bundle.Included selectedCount={selectedCount}>
            {selectedItems.map((selectedItem) => (
              <Bundle.SelectedItem
                key={selectedItem.selectionId}
                product={selectedItem}
                disabled={isLocked}
                onRemove={removeSelected}
              />
            ))}

            {canSelectMore ? (
              <Bundle.Slot label={content.selectTitle} disabled={isLocked} onPress={openSelect} />
            ) : null}
          </Bundle.Included>
        </Bundle.Content>

        <Bundle.Footer>
          <Bundle.AddToBagButton
            status={addToBagStatus}
            settledCount={settledProductCount}
            totalCount={selectedCount}
            disabled={selectedCount === 0}
            onPress={handleAddToBag}
          />
        </Bundle.Footer>

        <Bundle.CloseButton disabled={isLocked} onPress={navigateBack} />
      </Bundle.Container>

      <Bundle.SelectModal
        isVisible={isSelecting}
        title={content.selectTitle}
        products={products}
        selectedProductSlug={pendingProduct?.productSlug}
        onSelect={handleSelect}
        onClose={closeSelect}
        onContinue={confirmSelect}
      />
    </>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = Pick<RemoteData, 'content' | 'products' | 'productsBySlug' | 'isDelivery'> & {
  restaurantSlug: string;
};

type RemoteData = ReturnType<typeof useBundleRemoteData>;

type AddToBagSummary = BundleMachineTypes.AddToBagSummary;
